/**
 * A Light-DOM, form-associated checkbox list element that supports the following attributes:
 * @prop {string} name
 * @prop {boolean} required
 * @prop {boolean} disabled
 * @prop {boolean} readonly
 * @prop {number|null} min
 * @prop {number|null} max
 * 
 * @extends {HTMLElement}
 * @example
 * <checkbox-list name="fruits" required min="1" max="3">
 *   <fieldset>
 *     <legend>Choose fruits</legend>
 *     <div>
 *       <input type="checkbox" id="fruits_0" value="apple">
 *       <label for="fruits_0">Apple</label>
 *     </div>
 *     ...
 *   </fieldset>
 * </checkbox-list>
 */
export default class HTMLCheckboxListElement extends HTMLElement {
	static formAssociated = true;

	/** @type {ElementInternals} */
  #internals;

  /** @type {string[]} */
  #values = [];

  /** @type {number|null} */
  #min = null;

  /** @type {number|null} */
  #max = null;

  /** @type {boolean} */
  #required = false;
	
  /** @type {boolean} */
  #disabled = false;
	
  /** @type {boolean} */
  #readOnly = false;

  /** @type {string} */
  #name = "";

  constructor() {
    super();
    this.#internals = this.attachInternals();
  }

	/**
	 * @returns {string[]} The attributes tracked for changes by this element.
	 */
  static get observedAttributes() {
    return ["name", "required", "disabled", "readonly", "min", "max"];
  }

  /**
	 * Returns the values of each selected checkbox within the list as an array.
	 * @returns {string[]}
	 */
  get selectedValues() {
    return this.elements
      .filter(cb => cb.checked)
      .map(cb => cb.value);
  }

	/**
	 * @returns {string}
	 */
	get name() {
		return this.#name;
	}

	/**
	 * @param {string} value
	 */
	set name(value) {
		this.#name = value;
		this.setAttribute("name", this.#name);
	}

	/**
	 * @returns {boolean}
	 */
	get required() {
		return this.#required;
	}

	/**
	 * @param {boolean} value
	 */
	set required(value) {
		this.#setRequired(value, true);
	}

	/**
	 * @param {boolean} value 
	 * @param {boolean} attribute 
	 */
	#setRequired(value, attribute) {
		this.#required = value;
		if (attribute) {
			this.toggleAttribute("required", this.#required);
		}
		this.ariaRequired = this.#required ? "true" : null;

		const fieldset = /** @type {HTMLFieldSetElement} */ (this.querySelector(":scope fieldset"));
		if (fieldset) {
			fieldset.ariaRequired = this.#required ? "true" : null;
		}
	}

	/**
	 * @returns {boolean}
	 */
	get disabled() {
		return this.#disabled;
	}

	/**
	 * @param {boolean} value
	 */
	set disabled(value) {
		this.#setDisabled(value, true);
	}

	/**
	 * @param {boolean} value 
	 * @param {boolean} attribute 
	 */
	#setDisabled(value, attribute) {
		this.#disabled = value; // Same side effects as `disabled` setter, but w/o modifying `disabled` attribute.
		if (attribute) {
			this.toggleAttribute("disabled", this.#disabled);
		}
		this.ariaDisabled = this.#disabled ? "true" : null;

		const fieldset = /** @type {HTMLFieldSetElement} */ (this.querySelector(":scope fieldset"));
		if (fieldset) {
			fieldset.ariaDisabled = "true";
			fieldset.disabled = true;
		} else {
			this.elements.forEach(cb => cb.disabled = this.#disabled);
		}
	}

	/**
	 * @returns {boolean}
	 */
	get readOnly() {
		return this.#readOnly;
	}

	/**
	 * @param {boolean} value
	 */
	set readOnly(value) {
		this.#setReadOnly(value, true);
	}

	/**
	 * @param {boolean} value 
	 * @param {boolean} attribute 
	 */
	#setReadOnly(value, attribute) {
		this.#readOnly = value;
		if (attribute) {
			this.toggleAttribute("readonly", this.#readOnly);
		}
		this.ariaReadOnly = this.#readOnly ? "true" : null;

		const fieldset = /** @type {HTMLFieldSetElement} */ (this.querySelector(":scope fieldset"));
		const checkboxes = this.elements;
		if (fieldset) {
			fieldset.ariaReadOnly = this.#readOnly ? "true" : null;
		} else {
			checkboxes.forEach(cb => cb.disabled = this.#disabled);
		}

		if (this.#readOnly) {
			checkboxes.forEach(cb => {
				cb.addEventListener("click", this.#readOnlyClickHandler);
				cb.addEventListener("keydown", this.#readOnlyKeyHandler);
			});
		} else {
			checkboxes.forEach(cb => {
				cb.removeEventListener("click", this.#readOnlyClickHandler);
				cb.removeEventListener("keydown", this.#readOnlyKeyHandler);
			});
		}
	}

	/**
	 * @returns {number|null}
	 */
	get min() {
		return this.#min;
	}

	/**
	 * @param {number|null} value
	 */
	set min(value) {
		this.#min = value;
		if (this.#min === null) {
			this.removeAttribute("min");
		} else {
			this.setAttribute("min", this.#min.toString());
		}
	}

	/**
	 * @returns {number|null}
	 */
	get max() {
		return this.#max;
	}

	/**
	 * @param {number|null} value
	 */
	set max(value) {
		this.#max = value;
		if (this.#max === null) {
			this.removeAttribute("max");
		} else {
			this.setAttribute("max", this.#max.toString());
		}
	}


	/**
	 * Triggered when the element is mounted.
	 */
  connectedCallback() {
    this.setAttribute("role", "group");
    this.addEventListener("change", this.#handleChange);
    this.#updateFormValue();
  }

	/**
	 * Triggered when the element is unmounted.
	 */
  disconnectedCallback() {
    this.removeEventListener("change", this.#handleChange);
  }

	/**
	 * Triggered when the markup is modified.
	 * @param {string} name 
	 * @param {string|null} oldValue 
	 * @param {string|null} newValue 
	 */
  attributeChangedCallback(name, oldValue, newValue) {
		switch (name) {
			case "name":
				this.#name = newValue ?? "";
				break;
			case "required":
				this.#setRequired(newValue != null, false);
				break;
			case "disabled": {
				this.#setDisabled(newValue != null, false);
			} break;
			case "readonly": {
				this.#setReadOnly(newValue != null, false);
			} break;
			case "min":
				this.min = typeof(newValue) === "undefined"
					? this.hasAttribute("min") ? parseInt(this.getAttribute("min") ?? "", 10) : null
					: parseInt(newValue ?? "", 10);
				break;
			case "max":
				this.max = typeof(newValue) === "undefined"
					? this.hasAttribute('max') ? parseInt(this.getAttribute('max') ?? "", 10) : null
					: parseInt(newValue ?? "", 10);
				break;
			default:
				break;
		}

    this.#updateFormValue();
  }

  /**
	 * @param {Event} event
	 */
  #handleChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.type === "checkbox") {
      this.#updateFormValue();
    }
  };

	/**
	 * @param {MouseEvent} event
	 */
	#readOnlyClickHandler = (event) => {
		if (this.#readOnly) {
			event.preventDefault();
		}
	}

	/**
	 * @param {KeyboardEvent} event 
	 */
	#readOnlyKeyHandler = (event) => {
		if (this.#readOnly && [" ", "Enter"].includes(event.key)) {
			event.preventDefault();
		}
	}

	/**
	 * Updates the FormData entries contributed by this element.
	 * @returns {void}
	 */
  #updateFormValue() {
    const values = this.selectedValues;
    this.#values = values;
		this.toggleAttribute('checked', this.selectedValues.length > 0);
    const valid = this.#validate();
    if (valid) {
      const formData = new FormData();
      for (const v of values) {
        formData.append(this.name, v);
      }
      this.#internals.setFormValue(formData);
    } else {
      this.#internals.setFormValue(null);
    }
  }

  /**
	 * Returns all input element descendants of this element.
	 * @returns {HTMLInputElement[]}
	 */
  get elements() {
    return Array.from(this.querySelectorAll(':scope input[type="checkbox"]'));
  }

  /**
   * Validates the component and updates `ElementInternals` validity state.
   * @returns {boolean}
   */
  #validate() {
    const count = this.selectedValues.length;
    if (this.required && count === 0) {
      this.#internals.setValidity({ valueMissing: true }, "Please select at least one option.");
      return false;
    }
    if (this.min !== null && count < this.min) {
      this.#internals.setValidity({ rangeUnderflow: true }, `Select at least ${this.min} options.`);
      return false;
    }
    if (this.max !== null && count > this.max) {
      this.#internals.setValidity({ rangeOverflow: true }, `Select no more than ${this.max} options.`);
      return false;
    }
    this.#internals.setValidity({});
    return true;
  }

  /**
   * @returns {boolean}
   */
  checkValidity() {
    return this.#validate();
  }

  /**
   * Reports the validity and scrolls to the first invalid checkbox.
   * @returns {boolean}
   */
  reportValidity() {
    const valid = this.#validate();
    if (!valid) {
      const firstCheckbox = this.elements[0];
      if (firstCheckbox) {
        firstCheckbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstCheckbox.focus();
      }
      // Manually display the message (only works in supported browsers)
      this.#internals.reportValidity();
    }
    return valid;
  }

  /** Form API */
  get form() {
    return this.#internals.form;
  }

  get validity() {
    return this.#internals.validity;
  }

  get validationMessage() {
    return this.#internals.validationMessage;
  }

  get willValidate() {
    return this.#internals.willValidate;
  }

  /**
   * @param {string} message
   */
  setCustomValidity(message) {
    this.#internals.setValidity(message ? { customError: true } : {}, message);
  }
}
